import type { AuthUser } from "../auth/auth";
import type { RoomController, RoomControllerOptions } from "./types";

// The call is owned above the router outlet, so route changes never end it.
// There is at most one live room: a join error stays on its record, and
// `controller` is null only while loading or when the chunk failed to load.
export type ActiveCall = {
  roomId: string;
  controller: RoomController | null;
  error: string;
};
type CallState = { call: ActiveCall | null; maintenanceResets: number };
type CreateController = (_options: RoomControllerOptions) => RoomController;

// One shared import for prefetch and join. A failed import is forgotten so the
// next attempt retries it.
export function memoizeLoad<T>(load: () => Promise<T>) {
  let loading: Promise<T> | undefined;
  return () => {
    const current = (loading ??= load());
    current.catch(() => {
      if (loading === current) loading = undefined;
    });
    return current;
  };
}

export function createCallOwner({
  load,
  onMaintenance,
  now = () => performance.now(),
}: {
  load: () => Promise<CreateController>;
  onMaintenance: () => void;
  now?: () => number;
}) {
  let state: CallState = { call: null, maintenanceResets: 0 };
  let navigateHome = (_moved: boolean) => {};
  const listeners = new Set<() => void>();
  const setState = (next: CallState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  function end() {
    // Replace the record first so a re-entrant dispose cannot see a live call.
    const { call } = state;
    setState({ ...state, call: null });
    // A failed join already disposed its controller.
    if (!call?.error) call?.controller?.dispose();
  }

  function fail(roomId: string, error: string) {
    const { call } = state;
    if (call?.roomId !== roomId || call.error) return;
    call.controller?.dispose();
    if (error === "maintenance") {
      // Maintenance resets the entry so a fresh call starts once it ends.
      setState({ call: null, maintenanceResets: state.maintenanceResets + 1 });
      onMaintenance();
      return;
    }
    // The controller already released the room; keep the record so the error
    // screen stays until the route is left.
    setState({ ...state, call: { ...call, error } });
  }

  function ensureCall(roomId: string, user: AuthUser, authToken: string) {
    // A failed record is not a call: entering its room again starts a fresh join.
    if (state.call?.roomId === roomId && !state.call.error) return;
    end();
    const startedAt = now();
    const pending: ActiveCall = { roomId, controller: null, error: "" };
    setState({ ...state, call: pending });
    load().then(
      (createRoomController) => {
        if (state.call !== pending) return;
        const loadedAt = now();
        // A disposed controller can still report late (an in-flight request); only
        // the current call may end itself or navigate.
        const current = () => state.call?.controller === controller;
        const controller: RoomController = createRoomController({
          joinTiming: { startedAt, loadedAt },
          roomId,
          user,
          authToken,
          onJoinFailed: (code) => {
            if (current()) fail(roomId, code);
          },
          onLeft: (moved) => {
            if (!current()) return;
            end();
            navigateHome(moved);
          },
        });
        setState({ ...state, call: { ...pending, controller } });
        // The stage binds to the controller after rendering, but mounting is
        // idempotent and only starts the session.
        controller.mount();
      },
      () => {
        if (state.call === pending) fail(roomId, "room-connection-unavailable");
      },
    );
  }

  // A failed join is not a call; entering the room again starts over.
  function discardFailed(roomId: string) {
    if (state.call?.roomId === roomId && state.call.error) end();
  }

  return {
    ensureCall,
    endCall: end,
    discardFailed,
    // The room of a call that is loading, connecting or connected; a failed join is not a call.
    liveRoomId: () => (state.call && !state.call.error ? state.call.roomId : null),
    bindNavigation(navigate: (_moved: boolean) => void) {
      navigateHome = navigate;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
  };
}
